/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback, useMemo } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import {
  DropDown,
  NumericInput,
  PLACEMENT,
  Text,
  THEME_CONSTANTS,
  Toggle,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import Row from '../../../form/row';
import { SimplePanel } from '../../panel';
import {
  DEFAULT_LOTTIE_PLAYBACK,
  normalizeLottiePlayback,
} from '../../../../elements/lottie/contract';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import { getCommonValue, inputContainerStyleOverride } from '../../shared';

const Space = styled.div`
  flex: 0 0 12px;
`;

const FieldLabel = styled.label`
  flex: 1 1 auto;
`;

const StyledNumericInput = styled(NumericInput)`
  width: 100px;
`;

const StyledDropDown = styled(DropDown)`
  width: 128px;
`;

const getInputValue = (value) => (value === MULTIPLE_VALUE ? '' : value);

const getPlaceholder = (value, defaultValue = '') =>
  value === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : defaultValue;

function LottiePlaybackPanel({ selectedElements, pushUpdate }) {
  const autoplayId = useMemo(() => `cb-${uuidv4()}`, []);
  const loopId = useMemo(() => `cb-${uuidv4()}`, []);
  const playbackValues = useMemo(
    () =>
      selectedElements.map((element) =>
        normalizeLottiePlayback(element.playback)
      ),
    [selectedElements]
  );

  const autoplay = getCommonValue(playbackValues, 'autoplay');
  const loop = getCommonValue(playbackValues, 'loop');
  const loopCount = getCommonValue(playbackValues, 'loopCount');
  const startDelay = getCommonValue(playbackValues, 'startDelay');
  const holdDuration = getCommonValue(playbackValues, 'holdDuration');
  const speed = getCommonValue(playbackValues, 'speed');
  const endBehavior = getCommonValue(playbackValues, 'endBehavior');
  const endBehaviorOptions = useMemo(
    () => [
      { value: 'freeze', label: __('Freeze', 'web-stories') },
      { value: 'reset', label: __('Reset', 'web-stories') },
      { value: 'hide', label: __('Hide', 'web-stories') },
    ],
    []
  );

  const updatePlayback = useCallback(
    (update) => {
      pushUpdate((element) => {
        const currentPlayback = normalizeLottiePlayback(element.playback);
        const nextPlayback =
          typeof update === 'function'
            ? update(currentPlayback)
            : { ...currentPlayback, ...update };

        return {
          playback: normalizeLottiePlayback(nextPlayback),
        };
      }, true);
    },
    [pushUpdate]
  );

  const handleAutoplayChange = useCallback(
    (evt) => updatePlayback({ autoplay: evt.target.checked }),
    [updatePlayback]
  );

  const handleLoopChange = useCallback(
    (evt) => {
      const nextLoop = evt.target.checked;
      updatePlayback((currentPlayback) => ({
        ...currentPlayback,
        loop: nextLoop,
        ...(!nextLoop ? { loopCount: undefined } : {}),
      }));
    },
    [updatePlayback]
  );

  const handleLoopCountChange = useCallback(
    (_evt, value) => {
      updatePlayback((currentPlayback) => ({
        ...currentPlayback,
        loop: true,
        loopCount: value === '' ? undefined : value,
      }));
    },
    [updatePlayback]
  );

  const handleStartDelayChange = useCallback(
    (_evt, value) =>
      updatePlayback({
        startDelay: value === '' ? DEFAULT_LOTTIE_PLAYBACK.startDelay : value,
      }),
    [updatePlayback]
  );

  const handleSpeedChange = useCallback(
    (_evt, value) =>
      updatePlayback({
        speed: value === '' ? DEFAULT_LOTTIE_PLAYBACK.speed : value,
      }),
    [updatePlayback]
  );

  const handleHoldDurationChange = useCallback(
    (_evt, value) =>
      updatePlayback({
        holdDuration:
          value === '' ? DEFAULT_LOTTIE_PLAYBACK.holdDuration : value,
      }),
    [updatePlayback]
  );

  const handleEndBehaviorChange = useCallback(
    (_evt, value) => updatePlayback({ endBehavior: value }),
    [updatePlayback]
  );

  return (
    <SimplePanel name="lottiePlayback" title={__('Playback', 'web-stories')}>
      <Row>
        <FieldLabel htmlFor={autoplayId}>
          <Text as="span" size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('Autoplay', 'web-stories')}
          </Text>
        </FieldLabel>
        <Space />
        <Toggle
          id={autoplayId}
          checked={autoplay === true}
          onChange={handleAutoplayChange}
        />
      </Row>
      <Row>
        <FieldLabel htmlFor={loopId}>
          <Text as="span" size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('Loop', 'web-stories')}
          </Text>
        </FieldLabel>
        <Space />
        <Toggle
          id={loopId}
          checked={loop === true}
          onChange={handleLoopChange}
        />
      </Row>
      {loop === true && (
        <Row>
          <FieldLabel>
            <Text
              as="span"
              size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}
            >
              {__('Loop count', 'web-stories')}
            </Text>
          </FieldLabel>
          <Space />
          <StyledNumericInput
            aria-label={__('Loop count', 'web-stories')}
            value={getInputValue(loopCount)}
            allowEmpty
            min={2}
            onChange={handleLoopCountChange}
            onBlur={handleLoopCountChange}
            placeholder={getPlaceholder(
              loopCount,
              __('Forever', 'web-stories')
            )}
            isIndeterminate={loopCount === MULTIPLE_VALUE}
            containerStyleOverride={inputContainerStyleOverride}
          />
        </Row>
      )}
      <Row>
        <FieldLabel>
          <Text as="span" size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('Start delay', 'web-stories')}
          </Text>
        </FieldLabel>
        <Space />
        <StyledNumericInput
          aria-label={__('Start delay', 'web-stories')}
          value={getInputValue(startDelay)}
          allowEmpty
          isFloat
          min={0}
          suffix="s"
          onChange={handleStartDelayChange}
          onBlur={handleStartDelayChange}
          placeholder={getPlaceholder(startDelay)}
          isIndeterminate={startDelay === MULTIPLE_VALUE}
          containerStyleOverride={inputContainerStyleOverride}
        />
      </Row>
      <Row>
        <FieldLabel>
          <Text as="span" size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('Speed', 'web-stories')}
          </Text>
        </FieldLabel>
        <Space />
        <StyledNumericInput
          aria-label={__('Speed', 'web-stories')}
          value={getInputValue(speed)}
          allowEmpty
          isFloat
          min={0.25}
          max={4}
          suffix="x"
          onChange={handleSpeedChange}
          onBlur={handleSpeedChange}
          placeholder={getPlaceholder(speed)}
          isIndeterminate={speed === MULTIPLE_VALUE}
          containerStyleOverride={inputContainerStyleOverride}
        />
      </Row>
      <Row>
        <FieldLabel>
          <Text as="span" size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('End behavior', 'web-stories')}
          </Text>
        </FieldLabel>
        <Space />
        <StyledDropDown
          ariaLabel={__('End behavior', 'web-stories')}
          options={endBehaviorOptions}
          selectedValue={endBehavior === MULTIPLE_VALUE ? '' : endBehavior}
          placeholder={getPlaceholder(endBehavior)}
          onMenuItemClick={handleEndBehaviorChange}
          placement={PLACEMENT.TOP}
        />
      </Row>
      {(endBehavior === 'hide' || endBehavior === 'reset') && (
        <Row>
          <FieldLabel>
            <Text
              as="span"
              size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}
            >
              {__('Hold duration', 'web-stories')}
            </Text>
          </FieldLabel>
          <Space />
          <StyledNumericInput
            aria-label={__('Hold duration', 'web-stories')}
            value={getInputValue(holdDuration)}
            allowEmpty
            isFloat
            min={0}
            suffix="s"
            onChange={handleHoldDurationChange}
            onBlur={handleHoldDurationChange}
            placeholder={getPlaceholder(holdDuration)}
            isIndeterminate={holdDuration === MULTIPLE_VALUE}
            containerStyleOverride={inputContainerStyleOverride}
          />
        </Row>
      )}
    </SimplePanel>
  );
}

LottiePlaybackPanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default LottiePlaybackPanel;
